import { useMemo, useState } from 'react';
import {
  Box, Text, Stack, Group, Button, TextInput, Select, ActionIcon, Badge,
  Tooltip, Collapse, Paper, Menu, Divider,
} from '@mantine/core';
import { IconPlus, IconPencil, IconTrash, IconChevronDown, IconChevronRight, IconDotsVertical } from '@tabler/icons-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Node } from 'reactflow';
import { variablesApi, Variable } from '@/services/variables.api';

const TYPE_COLORS: Record<string, string> = {
  STRING: 'blue',
  NUMBER: 'green',
  BOOLEAN: 'orange',
};

interface Props {
  flowId: string;
  nodes?: Node[];
}

export default function VariablesPanel({ flowId, nodes = [] }: Props) {
  const queryClient = useQueryClient();
  const [globalOpen, setGlobalOpen] = useState(true);
  const [flowOpen, setFlowOpen] = useState(true);
  const [pageOpen, setPageOpen] = useState(true);

  const pageTitleById = useMemo(() => {
    const map = new Map<string, string>();
    nodes.forEach((n) => {
      if (n.type === 'page') map.set(n.id, n.data?.title || n.id);
    });
    return map;
  }, [nodes]);
  const [adding, setAdding] = useState<'GLOBAL' | 'FLOW' | null>(null);
  const [addingPage, setAddingPage] = useState<string | null>(null); // pageNodeId
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState({ name: '', type: 'STRING' as 'STRING' | 'NUMBER' | 'BOOLEAN', defaultValue: '' });

  const { data: variables = [] } = useQuery({
    queryKey: ['variables', 'flow', flowId],
    queryFn: () => variablesApi.getFlowVariables(flowId),
  });

  const globalVars = variables.filter((v) => v.scope === 'GLOBAL');
  const flowVars = variables.filter((v) => v.scope === 'FLOW');
  const pageVars = variables.filter((v) => v.scope === 'PAGE');

  const createMutation = useMutation({
    mutationFn: variablesApi.createVariable,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['variables'] });
      resetForm();
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Parameters<typeof variablesApi.updateVariable>[1] }) =>
      variablesApi.updateVariable(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['variables'] });
      resetForm();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: variablesApi.deleteVariable,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['variables'] }),
  });

  const resetForm = () => {
    setAdding(null);
    setAddingPage(null);
    setEditingId(null);
    setForm({ name: '', type: 'STRING', defaultValue: '' });
  };

  const handleAdd = (scope: 'GLOBAL' | 'FLOW') => {
    if (!form.name.trim()) return;
    createMutation.mutate({
      name: form.name,
      type: form.type,
      defaultValue: form.defaultValue || undefined,
      scope,
      flowId: scope === 'FLOW' ? flowId : undefined,
    });
  };

  const handleAddPage = (pageNodeId: string) => {
    if (!form.name.trim()) return;
    createMutation.mutate({
      name: form.name,
      type: form.type,
      defaultValue: form.defaultValue || undefined,
      scope: 'PAGE',
      flowId,
      pageNodeId,
    });
  };

  const handleUpdate = () => {
    if (!editingId || !form.name.trim()) return;
    updateMutation.mutate({ id: editingId, data: form });
  };

  const startEdit = (v: Variable) => {
    setEditingId(v.id);
    setAdding(null);
    setForm({ name: v.name, type: v.type, defaultValue: v.defaultValue || '' });
  };

  const renderVarItem = (v: Variable) => {
    if (editingId === v.id) {
      return (
        <Paper key={v.id} p={6} radius="sm" withBorder mb={4}>
          <Stack gap={4}>
            <TextInput size="xs" placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            <Group gap={4}>
              <Select
                size="xs"
                data={[{ value: 'STRING', label: 'Str' }, { value: 'NUMBER', label: 'Num' }, { value: 'BOOLEAN', label: 'Bool' }]}
                value={form.type}
                onChange={(v) => setForm({ ...form, type: (v as any) || 'STRING' })}
                w={70}
              />
              <TextInput size="xs" placeholder="Default" value={form.defaultValue} onChange={(e) => setForm({ ...form, defaultValue: e.target.value })} style={{ flex: 1 }} />
            </Group>
            <Group gap={4} justify="flex-end">
              <Button size="compact-xs" variant="default" onClick={resetForm}>Cancel</Button>
              <Button size="compact-xs" color="blue" onClick={handleUpdate} loading={updateMutation.isPending}>Save</Button>
            </Group>
          </Stack>
        </Paper>
      );
    }

    return (
      <Group key={v.id} gap={4} mb={2} px={4} py={2} style={{ borderRadius: 4, fontSize: 12, cursor: 'default' }}>
        <Badge size="xs" color={TYPE_COLORS[v.type]} variant="dot" style={{ flexShrink: 0 }}>
          {v.type[0]}
        </Badge>
        <Text size="xs" style={{ flex: 1 }} truncate>{v.name}</Text>
        <Menu position="bottom-end" shadow="sm" width={120}>
          <Menu.Target>
            <ActionIcon variant="subtle" color="gray" size="xs">
              <IconDotsVertical size={12} />
            </ActionIcon>
          </Menu.Target>
          <Menu.Dropdown>
            <Menu.Item leftSection={<IconPencil size={12} />} onClick={() => startEdit(v)}>Edit</Menu.Item>
            <Menu.Item leftSection={<IconTrash size={12} />} color="red" onClick={() => deleteMutation.mutate(v.id)}>Delete</Menu.Item>
          </Menu.Dropdown>
        </Menu>
      </Group>
    );
  };

  const renderAddForm = (scope: 'GLOBAL' | 'FLOW') => (
    <Paper p={6} radius="sm" withBorder mt={4}>
      <Stack gap={4}>
        <TextInput size="xs" placeholder="Variable name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        <Group gap={4}>
          <Select
            size="xs"
            data={[{ value: 'STRING', label: 'Str' }, { value: 'NUMBER', label: 'Num' }, { value: 'BOOLEAN', label: 'Bool' }]}
            value={form.type}
            onChange={(v) => setForm({ ...form, type: (v as any) || 'STRING' })}
            w={70}
          />
          <TextInput size="xs" placeholder="Default" value={form.defaultValue} onChange={(e) => setForm({ ...form, defaultValue: e.target.value })} style={{ flex: 1 }} />
        </Group>
        <Group gap={4} justify="flex-end">
          <Button size="compact-xs" variant="default" onClick={resetForm}>Cancel</Button>
          <Button size="compact-xs" color="blue" onClick={() => handleAdd(scope)} loading={createMutation.isPending}>Add</Button>
        </Group>
      </Stack>
    </Paper>
  );

  return (
    <Stack gap={0}>
      {/* Global Variables Section */}
      <Group
        gap={4}
        px={4}
        py={6}
        style={{ cursor: 'pointer' }}
        onClick={() => setGlobalOpen(!globalOpen)}
      >
        {globalOpen ? <IconChevronDown size={14} /> : <IconChevronRight size={14} />}
        <Text size="xs" fw={600} c="dimmed" tt="uppercase" style={{ letterSpacing: '0.05em', flex: 1 }}>
          Global ({globalVars.length})
        </Text>
        <Tooltip label="Add global variable" position="right">
          <ActionIcon
            size="xs"
            variant="subtle"
            color="blue"
            onClick={(e) => { e.stopPropagation(); setAdding('GLOBAL'); setEditingId(null); setForm({ name: '', type: 'STRING', defaultValue: '' }); }}
          >
            <IconPlus size={12} />
          </ActionIcon>
        </Tooltip>
      </Group>
      <Collapse in={globalOpen}>
        <Box pl={18}>
          {globalVars.map(renderVarItem)}
          {globalVars.length === 0 && adding !== 'GLOBAL' && (
            <Text size="xs" c="dimmed" fs="italic">No global variables</Text>
          )}
          {adding === 'GLOBAL' && renderAddForm('GLOBAL')}
        </Box>
      </Collapse>

      <Divider my={4} />

      {/* Flow Variables Section */}
      <Group
        gap={4}
        px={4}
        py={6}
        style={{ cursor: 'pointer' }}
        onClick={() => setFlowOpen(!flowOpen)}
      >
        {flowOpen ? <IconChevronDown size={14} /> : <IconChevronRight size={14} />}
        <Text size="xs" fw={600} c="dimmed" tt="uppercase" style={{ letterSpacing: '0.05em', flex: 1 }}>
          Flow ({flowVars.length})
        </Text>
        <Tooltip label="Add flow variable" position="right">
          <ActionIcon
            size="xs"
            variant="subtle"
            color="blue"
            data-testid="add-flow-var"
            onClick={(e) => { e.stopPropagation(); setAdding('FLOW'); setEditingId(null); setForm({ name: '', type: 'STRING', defaultValue: '' }); }}
          >
            <IconPlus size={12} />
          </ActionIcon>
        </Tooltip>
      </Group>
      <Collapse in={flowOpen}>
        <Box pl={18}>
          {flowVars.map(renderVarItem)}
          {flowVars.length === 0 && adding !== 'FLOW' && (
            <Text size="xs" c="dimmed" fs="italic">No flow variables</Text>
          )}
          {adding === 'FLOW' && renderAddForm('FLOW')}
        </Box>
      </Collapse>

      <Divider my={4} />

      {/* Page Variables Section (available flow-wide as PageName.varName) */}
      <Group gap={4} px={4} py={6} style={{ cursor: 'pointer' }} onClick={() => setPageOpen(!pageOpen)}>
        {pageOpen ? <IconChevronDown size={14} /> : <IconChevronRight size={14} />}
        <Text size="xs" fw={600} c="dimmed" tt="uppercase" style={{ letterSpacing: '0.05em', flex: 1 }}>
          Page ({pageVars.length})
        </Text>
      </Group>
      <Collapse in={pageOpen}>
        <Box pl={18}>
          {Array.from(pageTitleById.entries()).map(([pageNodeId, title]) => {
            const vars = pageVars.filter((v) => v.pageNodeId === pageNodeId);
            return (
              <Box key={pageNodeId} mb={4}>
                <Group gap={4} justify="space-between">
                  <Text size="xs" fw={600} c="dimmed" truncate style={{ flex: 1 }}>{title}</Text>
                  <Tooltip label="Add page variable" position="right">
                    <ActionIcon
                      size="xs"
                      variant="subtle"
                      color="blue"
                      onClick={() => { setAddingPage(pageNodeId); setAdding(null); setEditingId(null); setForm({ name: '', type: 'STRING', defaultValue: '' }); }}
                    >
                      <IconPlus size={12} />
                    </ActionIcon>
                  </Tooltip>
                </Group>
                <Box pl={8}>
                  {vars.map(renderVarItem)}
                  {addingPage === pageNodeId && (
                    <Paper p={6} radius="sm" withBorder mt={4}>
                      <Stack gap={4}>
                        <TextInput size="xs" placeholder="Variable name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                        <Group gap={4}>
                          <Select
                            size="xs"
                            data={[{ value: 'STRING', label: 'Str' }, { value: 'NUMBER', label: 'Num' }, { value: 'BOOLEAN', label: 'Bool' }]}
                            value={form.type}
                            onChange={(v) => setForm({ ...form, type: (v as any) || 'STRING' })}
                            w={70}
                          />
                          <TextInput size="xs" placeholder="Default" value={form.defaultValue} onChange={(e) => setForm({ ...form, defaultValue: e.target.value })} style={{ flex: 1 }} />
                        </Group>
                        <Group gap={4} justify="flex-end">
                          <Button size="compact-xs" variant="default" onClick={resetForm}>Cancel</Button>
                          <Button size="compact-xs" color="blue" onClick={() => handleAddPage(pageNodeId)} loading={createMutation.isPending}>Add</Button>
                        </Group>
                      </Stack>
                    </Paper>
                  )}
                </Box>
              </Box>
            );
          })}
          {pageTitleById.size === 0 && (
            <Text size="xs" c="dimmed" fs="italic">No pages yet</Text>
          )}
        </Box>
      </Collapse>
    </Stack>
  );
}
